import { Component, ViewEncapsulation, OnInit, OnChanges, OnDestroy, SimpleChanges, Input } from '@angular/core';
import {
  FileManagerService,
  FileNode,
  FileManagerListResponse,
  FlatNode
} from 'src/app/services/api/file-manager.service';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeModule } from '@angular/material/tree';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { AlertInfo } from 'src/app/models/alert-info';
import { Observable, Subject, finalize, takeUntil } from 'rxjs';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import {
  FeatureActionKey,
  FILE_MANAGER_CREATE_FOLDER_PROFILE_CODES,
  normalizeProfileCode
} from 'src/app/layouts/main-layout/components/sidebar/config/access-control.config';
import { FileManagerClearanceModalComponent } from './components/file-manager-clearance-modal/file-manager-clearance-modal.component';
import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { LoaderConfig } from 'src/app/components/widgets/loader/loader.config';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'file-manager',
  standalone: true,
  imports: [MatTreeModule, CommonModule, FormsModule, NgbAlert, FileManagerClearanceModalComponent, LoaderComponent],
  templateUrl: './file-manager.component.html',
  styleUrls: ['./file-manager.component.scss'],
})
export class FileManagerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() initialPath: string = 'it';
  @Input() showUploadButton: boolean = true;
  @Input() allowedActions: string[] = ['create-folder', 'rename', 'move', 'delete', 'upload', 'download'];
  @Input() featureKey: FeatureActionKey | null = null;

  canDelete = false;
  canModify = true;
  canCreateFolder = false;
  canManageClearance = false;

  clearanceNode: FlatNode | null = null;

  private destroy$ = new Subject<void>();

  currentPath: string = '';
  selectedNode: FlatNode | null = null;
  selectedNodePath: string = '';

  currentAction: string | null = null;
  destinationPath: string = '';
  newFolderName: string = '';
  newName: string = '';
  availableFolders: string[] = [];

  selectedDestination: FlatNode | null = null;
  selectedFile: FlatNode | null = null;
  nodeToMove: FlatNode | null = null;

  treeControl: FlatTreeControl<FlatNode>;
  treeFlattener: MatTreeFlattener<FileNode, FlatNode>;
  dataSource: MatTreeFlatDataSource<FileNode, FlatNode>;

  fileDeleted: string | null = null;
  folderToRename: string | null = null;
  newFolderPath: string | null = null;

  private nodeMap = new Map<string, FileNode>();

  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  isLoading = false;
  loaderConfig: LoaderConfig = {
    type: 'spinner',
    overlay: true,
    text: 'Loading...'
  };

  private readonly LOADER_CONTEXT = 'file-manager';
  private pendingRequests = 0;

  constructor(
    private fileManagerService: FileManagerService,
    private confirmationDialogService: ConfirmationDialogService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private loaderService: LoaderService,
  ) {
    console.log('[FileManagerComponent] Constructor - initialPath:', this.initialPath);
    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      node => node.level,
      node => node.expandable,
      node => node.children
    );
    this.treeControl = new FlatTreeControl<FlatNode>(
      node => node.level,
      node => node.expandable
    );
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  ngOnInit(): void {
    console.log('[FileManagerComponent] ngOnInit - initialPath:', this.initialPath);
    console.log('[FileManagerComponent] ngOnInit - currentPath:', this.currentPath);
    this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => this.isLoading = isLoading);

    if (!this.currentPath) {
      this.currentPath = this.initialPath;
      console.log('[FileManagerComponent] Setting currentPath to:', this.currentPath);
      this.loadFileList(this.currentPath);
    }

    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profile => {
        this.canDelete = this.featureKey
          ? this.accessControlService.hasFeatureActionAccess(profile, this.featureKey, 'delete')
          : profile === 'ADM1';
        this.canModify = this.featureKey
          ? this.accessControlService.hasFeatureActionAccess(profile, this.featureKey, 'modify', true)
          : true;
        this.canCreateFolder = this.featureKey
          ? this.accessControlService.hasFeatureActionAccess(profile, this.featureKey, 'createFolder')
          : FILE_MANAGER_CREATE_FOLDER_PROFILE_CODES.some(code => code === normalizeProfileCode(profile));
        this.canManageClearance = profile === 'ADM1';
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.pendingRequests = 0;
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
  }

  private withLoader<T>(text: string, request$: Observable<T>): Observable<T> {
    this.pendingRequests++;
    this.loaderConfig = { ...this.loaderConfig, text };
    this.loaderService.startLoading(this.LOADER_CONTEXT);

    return request$.pipe(
      finalize(() => {
        this.pendingRequests = Math.max(0, this.pendingRequests - 1);
        if (this.pendingRequests === 0) {
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('[FileManagerComponent] ngOnChanges:', changes);
    if (changes['initialPath'] && !changes['initialPath'].firstChange) {
      this.currentPath = changes['initialPath'].currentValue;
      this.loadFileList(this.currentPath);
    }
  }

  loadFileList(path: string, restorePaths?: string[]): void {
    const params = new HttpParams().set('path', path);
    this.withLoader('Loading...', this.fileManagerService.actionFolderFileList(params)).subscribe({
      next: (response: FileManagerListResponse) => {
        this.nodeMap.clear();
        this.dataSource.data = this.buildFileNodes(response);
        this.currentPath = path;
        if (restorePaths?.length) {
          setTimeout(() => this.restoreExpandedPaths([...restorePaths].sort(
            (a, b) => a.split('/').length - b.split('/').length
          ), 0));
        }
      },
      error: (error) => {
        this.handleActionError('load files', error);
      }
    });
  }

  private restoreExpandedPaths(sortedPaths: string[], index: number): void {
    if (index >= sortedPaths.length) return;

    const path = sortedPaths[index];
    const fileNode = this.nodeMap.get(path);

    if (!fileNode) {
      this.restoreExpandedPaths(sortedPaths, index + 1);
      return;
    }

    if (fileNode.children !== undefined) {
      const node = this.treeControl.dataNodes.find(n => n.path === path);
      if (node) this.treeControl.expand(node);
      setTimeout(() => this.restoreExpandedPaths(sortedPaths, index + 1));
      return;
    }

    const params = new HttpParams().set('path', path);
    this.withLoader('Loading...', this.fileManagerService.actionFolderFileList(params)).subscribe({
      next: (response: FileManagerListResponse) => {
        fileNode.children = this.buildFileNodes(response);
        this.dataSource.data = [...this.dataSource.data];
        setTimeout(() => {
          const node = this.treeControl.dataNodes.find(n => n.path === path);
          if (node) this.treeControl.expand(node);
          setTimeout(() => this.restoreExpandedPaths(sortedPaths, index + 1));
        });
      },
      error: () => this.restoreExpandedPaths(sortedPaths, index + 1)
    });
  }

  private buildFileNodes(response: FileManagerListResponse): FileNode[] {
    const folders = (response.data.paths || [])
      .filter(p => p.type === 'FOLDER')
      .map(p => {
        const node: FileNode = {
          name: p.name,
          type: 'folder',
          path: `${response.data.basePath}/${p.name}`
        };
        this.nodeMap.set(node.path!, node);
        return node;
      });
    const files = (response.data.paths || [])
      .filter(p => p.type === 'FILE')
      .map(p => ({
        name: p.name,
        type: 'file' as const,
        path: `${response.data.basePath}/${p.name}`
      }));
    return [...folders, ...files];
  }

  loadFolderChildren(flatNode: FlatNode): void {
    if (!flatNode.path) return;

    const fileNode = this.nodeMap.get(flatNode.path);
    if (!fileNode) return;

    // Already loaded: solo toggle
    if (fileNode.children !== undefined) {
      this.treeControl.toggle(flatNode);
      return;
    }

    const params = new HttpParams().set('path', flatNode.path);
    this.withLoader('Loading folder...', this.fileManagerService.actionFolderFileList(params)).subscribe({
      next: (response: FileManagerListResponse) => {
        fileNode.children = this.buildFileNodes(response);
        this.expandAndPreserveState(flatNode.path!);
      },
      error: (error) => this.handleActionError('load folder', error)
    });
  }

  private expandAndPreserveState(expandPath: string): void {
    const expandedPaths = this.treeControl.dataNodes
      .filter(n => this.treeControl.isExpanded(n))
      .map(n => n.path);

    this.dataSource.data = [...this.dataSource.data];

    setTimeout(() => {
      this.treeControl.dataNodes
        .filter(n => n.path && (expandedPaths.includes(n.path) || n.path === expandPath))
        .forEach(n => this.treeControl.expand(n));
    });
  }

  isActionAllowed(action: string): boolean {
    return this.allowedActions.includes(action);
  }

  transformer = (node: FileNode, level: number): FlatNode => {
    return {
      name: node.name,
      type: node.type,
      level: level,
      expandable: node.type === 'folder',
      id: node.id,
      path: node.path
    };
  };

  hasChild = (_: number, node: FlatNode) => node.expandable;

  selectNode(node: FlatNode): void {
    if (this.currentAction !== 'move' && this.currentAction !== 'delete') {
      if (node.type === 'file') {
        this.selectedFile = node;
        this.updateSelectedNodePath(node);
      } else if (node.type === 'folder') {
        this.selectedFile = null;
        this.loadFolderChildren(node);
      }
    } else if (node.type === 'folder') {
      this.selectedDestination = node;
    }

    this.selectedNode = node;
  }

  updateSelectedNodePath(node: FlatNode): void {
    if (node.path) {
      this.selectedNodePath = node.path;
    } else {
      const path: string[] = [];
      let currentNode: FlatNode | null = node;

      while (currentNode) {
        path.unshift(currentNode.name);
        currentNode = this.getParentNode(currentNode);
      }

      this.selectedNodePath = path.join('/');
    }
  }

  navigateToParent(): void {
    const pathParts = this.currentPath.split('/').filter(p => p);
    if (pathParts.length > 1) {
      pathParts.pop();
      const parentPath = pathParts.join('/');
      this.loadFileList(parentPath);
    }
  }

  getParentNode(node: FlatNode): FlatNode | null {
    const currentIndex = this.treeControl.dataNodes.indexOf(node);

    if (currentIndex > 0) {
      for (let i = currentIndex - 1; i >= 0; i--) {
        if (this.treeControl.getLevel(this.treeControl.dataNodes[i]) < this.treeControl.getLevel(node)) {
          return this.treeControl.dataNodes[i];
        }
      }
    }

    return null;
  }

  selectRootFolder(): void {
    const root: FlatNode = {
      name: this.initialPath,
      type: 'folder',
      level: 0,
      expandable: true,
      path: this.initialPath
    };
    if (this.currentAction === 'move') {
      this.selectedDestination = root;
    } else {
      this.selectedNode = root;
      this.selectedFile = null;
    }
  }

  isRootSelected(): boolean {
    return this.selectedNode?.path === this.initialPath;
  }

  setAction(action: string): void {
    this.currentAction = action;
    if (action === 'rename') {
      this.newName = this.selectedNode?.name ?? '';
    }
    if (action === 'move') {
      this.nodeToMove = this.selectedNode;
    }
  }

  cancelAction(): void {
    this.currentAction = null;
    this.selectedDestination = null;
    this.nodeToMove = null;
  }

  openClearanceForSelected(): void {
    if (this.selectedNode) {
      this.clearanceNode = this.selectedNode;
    }
  }

  onClearanceClosed(): void {
    this.clearanceNode = null;
  }

  selectRootAsDestination(): void {
    this.selectedDestination = {
      name: this.initialPath,
      type: 'folder',
      level: 0,
      expandable: true,
      path: this.initialPath
    };
  }

  confirmMove(): void {
    const source = this.nodeToMove;

    if (!source || !this.selectedDestination || !source.path || !this.selectedDestination.path) {
      console.error('Invalid item or destination selected.');
      this.showAlertInfo('warning', 'Please select a valid item and destination folder.');
      return;
    }

    if (source.type === 'folder' &&
      (this.selectedDestination.path === source.path || this.selectedDestination.path.startsWith(`${source.path}/`))) {
      this.showAlertInfo('warning', 'Cannot move a folder into itself or one of its subfolders.');
      return;
    }

    const message = `Do you really want to move "${source.name}" to "${this.selectedDestination.name}"?`;
    this.confirmationDialogService.open(message, () => {
      if (!source.path || !this.selectedDestination?.path) {
        return;
      }

      const oldPath = source.path;
      const newPath = `${this.selectedDestination.path}/${source.name}`;
      const params = new HttpParams().set('oldPath', oldPath).set('newPath', newPath);

      const moveCall = source.type === 'folder'
        ? this.fileManagerService.actionFolderMove(params)
        : this.fileManagerService.actionFileMove(params);

      this.withLoader('Moving...', moveCall).subscribe({
        next: () => {
          this.moveNodeInTree(oldPath, newPath, this.selectedDestination!.path!);
          this.handleActionSuccess('Move');
          this.cancelAction();
        },
        error: (error) => {
          console.error('Error moving item:', error);
          this.handleActionError('move', error);
          this.cancelAction();
        }
      });
    }, () => {
      this.cancelAction();
    });
  }

  confirmCreateFolder(): void {
    if (!this.canCreateFolder) {
      this.cancelAction();
      return;
    }

    if (!this.newFolderName.trim()) {
      this.showAlertInfo('warning', 'Folder name cannot be empty.');
      return;
    }

    const message = `Do you really want to create the folder "${this.newFolderName}"?`;
    this.confirmationDialogService.open(message, () => {
      const targetPath = (this.selectedNode?.type === 'folder' && this.selectedNode.path)
        ? this.selectedNode.path
        : this.currentPath;
      const path = `${targetPath}/${this.newFolderName}`;
      const params = new HttpParams().set('path', path);

      this.withLoader('Creating folder...', this.fileManagerService.actionFolderCreate(params)).subscribe({
        next: () => {
          if (this.selectedNode?.type === 'folder' && this.selectedNode.path) {
            const folderNode = this.nodeMap.get(this.selectedNode.path);
            if (folderNode) folderNode.children = undefined;
            this.loadFolderChildren(this.selectedNode as FlatNode);
          } else {
            this.loadFileList(this.currentPath);
          }
          this.handleActionSuccess('Create folder');
          this.newFolderName = '';
          this.cancelAction();
        },
        error: (error) => {
          console.error('Error creating folder:', error);
          this.handleActionError('create folder', error);
          this.cancelAction();
        }
      });
    }, () => {
      this.cancelAction();
    });
  }

  confirmRename(): void {
    if (!this.newName.trim()) {
      this.showAlertInfo('warning', 'Name cannot be empty.');
      return;
    }

    const message = `Do you really want to rename "${this.selectedNode?.name}" to "${this.newName}"?`;
    this.confirmationDialogService.open(message, () => {
      if (!this.selectedNode?.path) return;

      const oldPath = this.selectedNode.path;
      const pathParts = oldPath.split('/');
      pathParts[pathParts.length - 1] = this.newName;
      const newPath = pathParts.join('/');

      const isFile = this.selectedNode.type === 'file';
      const renameCall = isFile
        ? this.fileManagerService.actionFileRename(new HttpParams().set('oldPath', oldPath).set('newPath', newPath))
        : this.fileManagerService.actionFolderRename(new HttpParams().set('oldPath', oldPath).set('newPath', newPath));

      this.withLoader('Renaming...', renameCall).subscribe({
        next: () => {
          const expandedPaths = this.treeControl.dataNodes
            .filter(n => this.treeControl.isExpanded(n) && n.path)
            .map(n => n.path!.startsWith(oldPath)
              ? newPath + n.path!.slice(oldPath.length)
              : n.path!
            );

          this.renameNodeInTree(this.dataSource.data, oldPath, this.newName, newPath);

          this.dataSource.data = [...this.dataSource.data];

          setTimeout(() => {
            this.treeControl.dataNodes
              .filter(n => n.path && expandedPaths.includes(n.path))
              .forEach(n => this.treeControl.expand(n));
          });

          this.handleActionSuccess('Rename');
          this.newName = '';
          this.cancelAction();
        },
        error: (error) => {
          this.handleActionError('rename', error);
          this.cancelAction();
        }
      });
    }, () => {
      this.cancelAction();
    });
  }

  private renameNodeInTree(nodes: FileNode[], oldPath: string, newName: string, newPath: string): boolean {
    for (const node of nodes) {
      if (node.path === oldPath) {
        this.nodeMap.delete(oldPath);
        node.name = newName;
        node.path = newPath;
        if (node.type === 'folder') this.nodeMap.set(newPath, node);
        if (node.children?.length) {
          this.updateChildPaths(node.children, oldPath, newPath);
        }
        return true;
      }
      if (node.children?.length && this.renameNodeInTree(node.children, oldPath, newName, newPath)) {
        return true;
      }
    }
    return false;
  }

  private updateChildPaths(nodes: FileNode[], oldPrefix: string, newPrefix: string): void {
    for (const node of nodes) {
      if (!node.path) continue;
      this.nodeMap.delete(node.path);
      node.path = newPrefix + node.path.slice(oldPrefix.length);
      if (node.type === 'folder') this.nodeMap.set(node.path, node);
      if (node.children?.length) {
        this.updateChildPaths(node.children, oldPrefix, newPrefix);
      }
    }
  }

  private removeNodeFromTree(nodes: FileNode[], path: string): FileNode | null {
    const index = nodes.findIndex(n => n.path === path);
    if (index !== -1) {
      const [removed] = nodes.splice(index, 1);
      this.removeFromNodeMap(removed);
      return removed;
    }
    for (const node of nodes) {
      if (node.children?.length) {
        const removed = this.removeNodeFromTree(node.children, path);
        if (removed) return removed;
      }
    }
    return null;
  }

  private removeFromNodeMap(node: FileNode): void {
    if (node.path) this.nodeMap.delete(node.path);
    node.children?.forEach(child => this.removeFromNodeMap(child));
  }

  private getChildrenArray(path: string): FileNode[] | null {
    if (path === this.currentPath) return this.dataSource.data;
    const node = this.nodeMap.get(path);
    return node?.children !== undefined ? node.children : null;
  }

  private insertNodeSorted(children: FileNode[], node: FileNode): void {
    if (node.type === 'folder') {
      const firstFileIndex = children.findIndex(c => c.type === 'file');
      if (firstFileIndex === -1) children.push(node);
      else children.splice(firstFileIndex, 0, node);
    } else {
      children.push(node);
    }
  }

  private moveNodeInTree(oldPath: string, newPath: string, destinationPath: string): void {
    const expandedPaths = this.treeControl.dataNodes
      .filter(n => this.treeControl.isExpanded(n) && n.path)
      .map(n => n.path!.startsWith(oldPath)
        ? newPath + n.path!.slice(oldPath.length)
        : n.path!
      );

    const movedNode = this.removeNodeFromTree(this.dataSource.data, oldPath);
    if (!movedNode) {
      this.loadFileList(this.currentPath);
      return;
    }

    movedNode.path = newPath;
    if (movedNode.type === 'folder') this.nodeMap.set(newPath, movedNode);
    if (movedNode.children?.length) {
      this.updateChildPaths(movedNode.children, oldPath, newPath);
    }

    const destinationChildren = this.getChildrenArray(destinationPath);
    if (destinationChildren) {
      this.insertNodeSorted(destinationChildren, movedNode);
    }

    if (this.selectedFile?.path === oldPath) this.selectedFile = null;
    if (this.selectedNode?.path === oldPath) this.selectedNode = null;

    this.dataSource.data = [...this.dataSource.data];

    setTimeout(() => {
      this.treeControl.dataNodes
        .filter(n => n.path && expandedPaths.includes(n.path))
        .forEach(n => this.treeControl.expand(n));
    });
  }

  private removeNodeAndRefreshTree(path: string): void {
    const expandedPaths = this.treeControl.dataNodes
      .filter(n => this.treeControl.isExpanded(n) && n.path && n.path !== path && !n.path.startsWith(`${path}/`))
      .map(n => n.path!);

    this.removeNodeFromTree(this.dataSource.data, path);

    if (this.selectedFile?.path === path) this.selectedFile = null;
    if (this.selectedNode?.path === path || this.selectedNode?.path?.startsWith(`${path}/`)) {
      this.selectedNode = null;
    }

    this.dataSource.data = [...this.dataSource.data];

    setTimeout(() => {
      this.treeControl.dataNodes
        .filter(n => n.path && expandedPaths.includes(n.path))
        .forEach(n => this.treeControl.expand(n));
    });
  }

  confirmDelete(): void {
    const nodeToDelete = this.selectedNode;
    const message = `Do you really want to delete "${nodeToDelete?.name}"?`;
    this.confirmationDialogService.open(message, () => {
      if (!nodeToDelete?.path) return;

      const params = new HttpParams().set('path', nodeToDelete.path);
      const deleteCall = nodeToDelete.type === 'file'
        ? this.fileManagerService.actionFileDelete(params)
        : this.fileManagerService.actionFolderDelete(params);

      this.withLoader('Deleting...', deleteCall).subscribe({
        next: () => {
          this.removeNodeAndRefreshTree(nodeToDelete.path!);
          this.handleActionSuccess('Delete');
          this.cancelAction();
        },
        error: (error) => {
          this.handleActionError('delete', error);
          this.cancelAction();
        }
      });
    }, () => {
      this.cancelAction();
    });
  }

  confirmUpload(): void {
    console.log('Uploading file');
    this.cancelAction();
  }

  downloadFile(): void {
    if (!this.selectedFile?.path) return;

    const params = new HttpParams().set('path', this.selectedFile.path);
    this.withLoader('Preparing download...', this.fileManagerService.actionFileDetail(params)).subscribe({
      next: (response: any) => {
        const url = response?.data?.file?.publicUrl;
        if (url) {
          window.open(url, '_blank');
        } else {
          this.showAlertInfo('warning', 'File URL not available.');
        }
      },
      error: (error: any) => {
        this.handleActionError('download', error);
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      input.value = '';
      const targetPath = (this.selectedNode?.type === 'folder' && this.selectedNode.path)
        ? this.selectedNode.path
        : this.currentPath;
      const path = `${targetPath}/${file.name}`;
      const params = new HttpParams().set('path', path);

      this.withLoader('Uploading file...', this.fileManagerService.actionFileCreate(file, params)).subscribe({
        next: () => {
          if (this.selectedNode?.type === 'folder' && this.selectedNode.path) {
            const folderNode = this.nodeMap.get(this.selectedNode.path);
            if (folderNode) {
              folderNode.children = undefined;
              this.loadFolderChildren(this.selectedNode as FlatNode);
            } else {
              this.loadFileList(this.currentPath);
            }
          } else {
            this.loadFileList(this.currentPath);
          }
          this.handleActionSuccess('Upload');
        },
        error: (error) => {
          this.handleActionError('upload', error);
        }
      });
    }
  }

  showAlertInfo(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }

  handleActionSuccess(action: string): void {
    this.showAlertInfo('success', `${action} completed successfully.`);
  }

  handleActionError(action: string, error: any): void {
    const errorMessage = error?.error?.message || error?.message || 'Unknown error';
    this.showAlertInfo('danger', `Failed to ${action}: ${errorMessage}`);
  }

  performAction(action: string): void {
    try {
      this.handleActionSuccess(action);
    } catch (error) {
      this.handleActionError(action, error);
    }
  }
}
